import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { RoleScope } from 'further-types/admin-user-role';
import { Typography, Button, Grid } from '@mui/material';

import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import {
  AppSimpleSelect,
  AppTextInput,
  FieldRow,
} from 'components/FormElements';
import api from 'lib/trpcClient';
import { ADMIN_USER_ROLES } from 'adminConstants/routes';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useGetAllPermissionsList } from 'hooks/ui/usePermissions';
import Spacing from 'components/Spacing';
import Row from 'components/Layout/Row';
import PermissionGroupBox from './components/PermissionGroupBox';
import { FormData, isLpPermission, isEisPermission } from './types';
import {
  eisPermissionGroups,
  eisNonGroupedPermissions,
} from './permission-groups-config';
import {
  lpPermissionGroups,
  lpNonGroupedPermissions,
} from './lp-permission-groups-config';
import { FirmSelector } from 'components/FirmSelector';
import { adminUserRole as rules } from 'further-ui/validations';
import { AdminUserRealm } from 'further-types/admin-user';
import { Realm } from 'further-types/global';
import { RefinementCtx } from 'zod';

const AddEditRolePage: React.FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const userPermissions = useGetAllPermissionsList();
  const [scope, setScope] = useState<RoleScope>(
    isSuperAdmin ? RoleScope.Global : RoleScope.Firm,
  );

  const createMutationOptions = useHandleTrpcMutation()({
    successMessage: 'Role created successfully.',
    successRedirect: ADMIN_USER_ROLES,
  });

  const updateMutationOptions = useHandleTrpcMutation()({
    successMessage: 'Role updated successfully.',
    successRedirect: ADMIN_USER_ROLES,
  });

  const createEisRole = api.adminUserRole.createEisRole.useMutation(
    createMutationOptions,
  );
  const createLpRole = api.adminUserRole.createLpRole.useMutation(
    createMutationOptions,
  );
  const updateEisRole = api.adminUserRole.updateEisRole.useMutation(
    updateMutationOptions,
  );
  const updateLpRole = api.adminUserRole.updateLpRole.useMutation(
    updateMutationOptions,
  );

  const { id, newRoleRealm } = useParams<{
    id?: string;
    newRoleRealm?: AdminUserRealm;
  }>();
  const isRoleBeingEdited = !!id;

  const { data: role, isFetching: isEditedRoleFetching } =
    api.adminUserRole.get.useQuery(
      { roleId: id ?? '' },
      { enabled: isRoleBeingEdited },
    );

  const realm = isRoleBeingEdited ? role?.realm : newRoleRealm;
  const allPermissionGroups =
    realm === AdminUserRealm.Eis
      ? eisPermissionGroups
      : realm === AdminUserRealm.Lp
      ? lpPermissionGroups
      : [];
  const permissionGroups = allPermissionGroups.filter(
    (group) => !group.autoEnabled,
  );
  const autoEnabledPermissions = allPermissionGroups
    .filter((group) => group.autoEnabled)
    .flatMap((group) => group.groupItems.flatMap((item) => item.permissions));
  const nonGroupedPermissions =
    realm === AdminUserRealm.Eis
      ? eisNonGroupedPermissions
      : realm === AdminUserRealm.Lp
      ? lpNonGroupedPermissions
      : { groupItems: [], groupName: '', groupDescription: '' };
  const allGroupItems = [
    ...allPermissionGroups.flatMap((group) => group.groupItems),
    ...nonGroupedPermissions.groupItems,
  ];
  const pageTitle = isRoleBeingEdited
    ? `Edit ${realm ?? ''} Role`
    : `Create ${realm ?? ''} Role`;

  // check the permissions are valid according to the enum (there may be old permissions in the DB that are no longer in the enum)
  const cleanPermissions = useMemo(() => {
    if (!role?.permissions) {
      return [];
    }
    if (realm === AdminUserRealm.Eis) {
      return role.permissions.filter(isEisPermission);
    } else if (realm === AdminUserRealm.Lp) {
      return role.permissions.filter(isLpPermission);
    }
    return [];
  }, [role?.permissions, realm]);

  useEffect(() => {
    if (role?.scope === RoleScope.Firm) {
      setScope(RoleScope.Firm);
    }
  }, [role?.scope]);

  const checkFirmId = (data: { firmId?: string }, ctx: RefinementCtx) => {
    if (!isRoleBeingEdited && scope === RoleScope.Firm && !data.firmId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Firm is required for firm specific roles.',
        path: ['firmId'],
      });
    }
  };
  const form = useForm<FormData>({
    values: isRoleBeingEdited
      ? {
          name: role?.name ?? '',
          description: role?.description ?? '',
          permissions: [
            ...new Set([...cleanPermissions, ...autoEnabledPermissions]),
          ],
          firmId: role?.firmId ?? role?.lpFirmId,
        }
      : {
          name: '',
          description: '',
          permissions: [...autoEnabledPermissions],
          firmId,
        },
    resolver: zodResolver(
      realm === AdminUserRealm.Lp
        ? rules.CreateLpRoleInput.superRefine(checkFirmId)
        : rules.CreateEisRoleInput.superRefine(checkFirmId),
    ),
  });

  const onCreateRole = (data: FormData) => {
    if (realm === AdminUserRealm.Lp) {
      createLpRole.mutate({
        ...data,
        permissions: data.permissions.filter(isLpPermission),
      });
    } else if (realm === AdminUserRealm.Eis) {
      createEisRole.mutate({
        ...data,
        permissions: data.permissions.filter(isEisPermission),
      });
    }
  };

  const onUpdateRole = (data: FormData) => {
    if (!id) {
      return;
    }

    if (realm === AdminUserRealm.Lp) {
      updateLpRole.mutate({
        roleId: id,
        description: data.description,
        permissions: data.permissions.filter(isLpPermission),
      });
    } else if (realm === AdminUserRealm.Eis) {
      updateEisRole.mutate({
        roleId: id,
        description: data.description,
        permissions: data.permissions.filter(isEisPermission),
      });
    }
  };

  if (isEditedRoleFetching) {
    return <PageContainer heading={pageTitle} loading={true} />;
  }

  if (!realm) {
    return <div>No realm selected</div>;
  }

  return (
    <PageContainer heading={pageTitle}>
      <PageContent withSpacing>
        <form
          onSubmit={form.handleSubmit(
            isRoleBeingEdited ? onUpdateRole : onCreateRole,
          )}
        >
          <Grid container spacing={2}>
            <Grid size={8}>
              <Spacing size="md">
                <FieldRow title="Role Name" centerTitle>
                  <Controller
                    control={form.control}
                    name="name"
                    disabled={isRoleBeingEdited}
                    render={({
                      fieldState: { error },
                      field: { ref, ...rest },
                    }) => (
                      <AppTextInput
                        {...rest}
                        error={!!error}
                        helperText={error?.message?.toString()}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow title="Description" centerTitle>
                  <Controller
                    control={form.control}
                    name="description"
                    render={({
                      fieldState: { error },
                      field: { ref, ...rest },
                    }) => (
                      <AppTextInput
                        {...rest}
                        error={!!error}
                        helperText={error?.message?.toString()}
                      />
                    )}
                  />
                </FieldRow>

                {isSuperAdmin ? (
                  <FieldRow title="Role scope" centerTitle>
                    <AppSimpleSelect
                      fullWidth
                      disabled={isRoleBeingEdited}
                      options={[
                        {
                          value: RoleScope.Global,
                          label: 'System-wide',
                        },
                        { value: RoleScope.Firm, label: 'Firm-specific' },
                      ]}
                      value={scope}
                      onChange={(e) => {
                        const newScope = e.target.value as RoleScope;
                        setScope(newScope);
                        if (newScope === RoleScope.Global) {
                          form.setValue('firmId', undefined);
                        }
                      }}
                    />
                  </FieldRow>
                ) : null}

                {isSuperAdmin && scope === RoleScope.Firm ? (
                  <FieldRow title="Firm" centerTitle>
                    <Controller
                      control={form.control}
                      disabled={isRoleBeingEdited}
                      name="firmId"
                      render={({
                        fieldState: { error },
                        field: { ref, value, ...rest },
                      }) => (
                        <FirmSelector
                          {...rest}
                          firmId={value}
                          error={!!error}
                          firmRealmsToShow={
                            // AdminUserRealm includes SUPER, but it won't be possible to
                            // create/edit SUPER roles. Cast to simple `Realm` instead.
                            realm === AdminUserRealm.Eis
                              ? Realm.Eis
                              : realm === AdminUserRealm.Lp
                              ? Realm.Lp
                              : undefined
                          }
                          helperText={error?.message?.toString()}
                        />
                      )}
                    />
                  </FieldRow>
                ) : null}

                <Row justify="space-between">
                  <Typography variant="h2">Permissions</Typography>
                  <Button variant="contained" color="primary" type="submit">
                    {isRoleBeingEdited ? 'Save changes' : 'Create role'}
                  </Button>
                </Row>

                {permissionGroups.map((group) => (
                  <PermissionGroupBox
                    key={group.groupName}
                    title={group.groupName}
                    description={group.groupDescription}
                    items={group.groupItems}
                    allItems={allGroupItems}
                    form={form}
                    userPermissions={userPermissions ?? []}
                  />
                ))}

                {nonGroupedPermissions.groupItems.length > 0 ? (
                  <PermissionGroupBox
                    title={nonGroupedPermissions.groupName}
                    description={nonGroupedPermissions.groupDescription}
                    items={nonGroupedPermissions.groupItems}
                    allItems={allGroupItems}
                    form={form}
                    userPermissions={userPermissions ?? []}
                  />
                ) : null}
              </Spacing>
            </Grid>
          </Grid>
        </form>
      </PageContent>
    </PageContainer>
  );
};

export default AddEditRolePage;
